/**
 * Stone Investment Blog Article
 *
 * Legacy BlogArticle with Stone's editorial aesthetic:
 * Cormorant Garamond headings, greyscale featured image,
 * minimal typography, square corners.
 */

'use client';

import type { BlogArticleProps } from '@routeroyal/theme-core';
import { sanitizeHTML } from '@routeroyal/ui-core';
import Image from 'next/image';
import { StoneBlogCard } from './StoneBlogCard';

export function StoneBlogArticle({
  article,
  relatedArticles = [],
  onShare,
  locale = 'en',
}: BlogArticleProps) {
  const formattedDate = new Date(article.publishedAt).toLocaleDateString(
    locale === 'fr' ? 'fr-FR' : 'en-GB',
    { day: 'numeric', month: 'long', year: 'numeric' }
  );

  return (
    <article className="max-w-4xl mx-auto">
      {/* Header */}
      <header className="mb-12 text-center">
        {article.category && (
          <span
            className="text-[10px] tracking-[0.25em] uppercase text-stone-500 mb-4 block"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {article.category}
          </span>
        )}

        <h1
          className="text-4xl md:text-5xl lg:text-6xl text-stone-900 mb-6 leading-tight"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400 }}
        >
          {article.title}
        </h1>

        {article.excerpt && (
          <p
            className="text-lg text-stone-600 mb-8 leading-relaxed max-w-2xl mx-auto"
            style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
          >
            {article.excerpt}
          </p>
        )}

        {/* Meta */}
        <div
          className="flex flex-wrap items-center justify-center gap-4 text-xs text-stone-500 border-t border-b border-stone-200 py-4"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          <div className="flex items-center gap-3">
            {article.author.avatar && (
              <div className="relative w-10 h-10 overflow-hidden">
                <Image
                  src={article.author.avatar}
                  alt={article.author.name}
                  fill
                  className="object-cover grayscale"
                />
              </div>
            )}
            <span className="text-stone-900">{article.author.name}</span>
          </div>
          <span className="text-stone-300">·</span>
          <time dateTime={article.publishedAt}>{formattedDate}</time>
          {article.readTime && (
            <>
              <span className="text-stone-300">·</span>
              <span>{article.readTime}</span>
            </>
          )}
          {onShare && (
            <button
              onClick={() => onShare('twitter')}
              className="ml-auto px-4 py-2 text-xs uppercase tracking-[0.15em] border border-stone-300 text-stone-700 hover:bg-stone-100 transition-colors"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Share
            </button>
          )}
        </div>
      </header>

      {/* Featured Image */}
      {article.featuredImage && (
        <div className="relative w-full h-[400px] md:h-[500px] lg:h-[600px] mb-12 overflow-hidden">
          <Image
            src={article.featuredImage}
            alt={article.title}
            fill
            className="object-cover"
            priority
          />
        </div>
      )}

      {/* Content */}
      <div
        className="
          prose prose-lg max-w-none mb-12
          prose-headings:font-['Cormorant_Garamond','Georgia',serif]
          prose-headings:text-stone-900
          prose-headings:font-normal
          prose-p:font-['Montserrat',sans-serif]
          prose-p:text-stone-700
          prose-p:leading-relaxed
          prose-a:text-stone-900
          prose-a:underline
          prose-a:underline-offset-4
          hover:prose-a:text-stone-500
          prose-strong:text-stone-900
          prose-blockquote:border-l-stone-400
          prose-blockquote:border-l-2
          prose-blockquote:bg-stone-50
          prose-blockquote:py-3 prose-blockquote:px-6 prose-blockquote:my-6
          prose-blockquote:text-stone-700
          prose-blockquote:not-italic
          prose-blockquote:font-['Cormorant_Garamond','Georgia',serif]
          prose-blockquote:text-xl
          prose-code:text-stone-900
          prose-code:bg-stone-100
          prose-code:px-1.5 prose-code:py-0.5
          prose-pre:bg-stone-900 prose-pre:text-stone-100
          prose-img:my-8
          prose-hr:border-stone-200 prose-hr:my-10
          prose-li:font-['Montserrat',sans-serif]
          prose-li:text-stone-700
          prose-li:marker:text-stone-400
        "
        dangerouslySetInnerHTML={{ __html: sanitizeHTML(article.content) }}
      />

      {/* Tags */}
      {article.tags && article.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-12 pb-12 border-b border-stone-200">
          {article.tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1 text-xs tracking-wide text-stone-700 border border-stone-300 hover:bg-stone-100 transition-colors"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              #{tag}
            </span>
          ))}
        </div>
      )}

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="mt-16 pt-12 border-t border-stone-200">
          <h2
            className="text-3xl text-stone-900 mb-8 text-center"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 400 }}
          >
            Related Articles
          </h2>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {relatedArticles.map((relatedArticle) => (
              <StoneBlogCard
                key={relatedArticle.id}
                article={relatedArticle}
                variant="compact"
                showExcerpt={true}
                showCategory={true}
                showAuthor={false}
                locale={locale}
              />
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
